import { TabContent, TabPane } from 'reactstrap';
import { BasicAlert } from '@g17eco/molecules/alert';
import { useAppSelector } from '../../../../../reducers';
import { InitiativePermissions } from '../../../../../services/permissions/InitiativePermissions';
import { DataConnectionsTab } from './DataConnectionsTab';
import { SemanticModelsTab } from './SemanticModelsTab';
import { AddDataConnectionTab } from './AddDataConnectionTab';
import { Menu } from '@g17eco/molecules/menu/Menu';
import { FeaturePermissions } from '../../../../../services/permissions/FeaturePermissions';
import { useGetDataIntegrationStatusQuery } from '../../../../../api/initiative-data-integrations';
import { AIChat } from '@features/ai/AIChat/AIChat';
import { useMemo } from 'react';
import { generateId } from '../../../../../utils';
import { useCurrentUser } from '../../../../../hooks/useCurrentUser';
import { getRootConfig } from '../../../../../selectors/globalData';

export enum DataIntegrationsTab {
  Connections = 'connections',
  Connectors = 'connectors',
  SemanticModels = 'semantic-models',
  AIChat = 'ai-chat',
}

interface DataIntegrationsProps {
  initiativeId: string;
  handleTabChange: (tab: DataIntegrationsTab) => void;
  activeTab: DataIntegrationsTab;
}

export const DataIntegrations = (props: DataIntegrationsProps) => {
  const { initiativeId, handleTabChange, activeTab } = props;

  const currentUser = useCurrentUser();
  const canManageDataIntegrations = currentUser
    ? InitiativePermissions.canManageInitiative(currentUser, initiativeId)
    : false;
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessDataIntegrations = FeaturePermissions.canAccessDataIntegrations(rootConfig);

  const { data: dataIntegrationStatus } = useGetDataIntegrationStatusQuery({
    initiativeId,
  });

  const isDataIntegrationEnabled = dataIntegrationStatus?.enabled;
  const randomId = useMemo(() => generateId(), []);
  const conversationId = `${currentUser._id}-${randomId}`;

  if (!canAccessDataIntegrations) {
    return (
      <BasicAlert type='info'>
        <i className='fal fa-info-circle me-2' />
        You do not have access to data integrations. Please contact your administrator to enable this
        feature.
      </BasicAlert>
    );
  }

  if (!canManageDataIntegrations) {
    return (
      <BasicAlert type='info'>
        <i className='fal fa-info-circle me-2' />
        You do not have permission to manage data integrations. Please contact your administrator to enable this
        feature.
      </BasicAlert>
    );
  }

  if (!isDataIntegrationEnabled) {
    return (
      <BasicAlert type='info'>
        <i className='fal fa-info-circle me-2' />
        Data integration service is not enabled. Enable it using the toggle switch above to start connecting your
        data sources.
      </BasicAlert>
    );
  }

  return (
      <>
        <Menu
          className='mb-4'
          items={[
            {
              label: 'My Data Connections',
              onClick: () => handleTabChange(DataIntegrationsTab.Connections),
              active: activeTab === DataIntegrationsTab.Connections,
            },
            {
              label: 'Add Data Connection',
              onClick: () => handleTabChange(DataIntegrationsTab.Connectors),
              active: activeTab === DataIntegrationsTab.Connectors,
            },
            {
              label: 'Semantic Model',
              onClick: () => handleTabChange(DataIntegrationsTab.SemanticModels),
              active: activeTab === DataIntegrationsTab.SemanticModels,
            },
            {
              label: 'AI Chat',
              onClick: () => handleTabChange(DataIntegrationsTab.AIChat),
              active: activeTab === DataIntegrationsTab.AIChat,
            },
          ]}
        />

        <TabContent activeTab={activeTab}>
          <TabPane tabId='connections'>
            {activeTab === DataIntegrationsTab.Connections ? <DataConnectionsTab initiativeId={initiativeId} /> : null}
          </TabPane>
          <TabPane tabId='connectors'>
            {activeTab === DataIntegrationsTab.Connectors ? (
              <AddDataConnectionTab initiativeId={initiativeId} handleBack={() => handleTabChange(DataIntegrationsTab.Connections)} />
            ) : null}
          </TabPane>
          <TabPane tabId='semantic-models'>
            {activeTab === DataIntegrationsTab.SemanticModels ? <SemanticModelsTab initiativeId={initiativeId} /> : null}
          </TabPane>
          <TabPane tabId='ai-chat'>
            {activeTab === DataIntegrationsTab.AIChat ? <AIChat initiativeId={initiativeId} conversationId={conversationId} /> : null}
          </TabPane>
        </TabContent>
      </>
  );
};
